import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, ShieldOff, ShieldCheck } from "lucide-react";
import { DashboardShell } from "@/components/DashboardShell";
import { SUPER_ADMIN_NAV } from "@/lib/nav";
import { fetchCustomers, setAccountStatus } from "@/lib/super-admin";

export const Route = createFileRoute("/_authenticated/super-admin/customers")({
  head: () => ({
    meta: [
      { title: "Customers · Landoo Super Admin" },
      { name: "description", content: "View all customers and manage account status." },
    ],
  }),
  component: CustomersPage,
});

function CustomersPage() {
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({
    queryKey: ["super-admin", "customers"],
    queryFn: fetchCustomers,
  });
  const [q, setQ] = useState("");

  const rows = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return data;
    return data.filter(
      (c) =>
        (c.full_name ?? "").toLowerCase().includes(t) ||
        (c.email ?? "").toLowerCase().includes(t) ||
        (c.mobile ?? "").includes(t),
    );
  }, [data, q]);

  const toggle = async (id: string, next: "active" | "suspended") => {
    try {
      await setAccountStatus(id, next);
      toast.success(next === "suspended" ? "Customer suspended" : "Customer restored");
      qc.invalidateQueries({ queryKey: ["super-admin", "customers"] });
      qc.invalidateQueries({ queryKey: ["super-admin", "stats"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not update");
    }
  };

  return (
    <DashboardShell title="Customers" role="Super Admin" items={SUPER_ADMIN_NAV}>
      <div className="relative mb-3 max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search customers…"
          className="w-full rounded-full border border-border bg-card py-2 pl-9 pr-3 text-xs outline-none focus:border-brand"
        />
      </div>

      {isLoading ? (
        <div className="rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground">
          Loading customers…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-6 text-sm text-muted-foreground">
          No customers found.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((c) => {
            const status = c.account_status ?? "active";
            const suspended = status === "suspended";
            return (
              <div key={c.id} className="rounded-2xl border border-border bg-card p-4 shadow-soft">
                <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3 sm:flex sm:flex-wrap sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                          suspended ? "bg-coral/15 text-coral" : "bg-rent/15 text-rent"
                        }`}
                      >
                        {suspended ? "Suspended" : "Active"}
                      </span>
                    </div>
                    <h3 className="mt-1 truncate text-sm font-semibold text-brown-dark">
                      {c.full_name ?? "Unnamed customer"}
                    </h3>
                    <p className="truncate text-xs text-muted-foreground">
                      {c.email ?? "—"} · {c.mobile ?? "—"} · {c.city ?? "—"}
                    </p>
                  </div>
                  <div className="flex flex-wrap justify-end gap-1.5">
                    {suspended ? (
                      <button
                        onClick={() => toggle(c.id, "active")}
                        className="inline-flex items-center gap-1 rounded-full bg-rent px-3 py-1.5 text-xs font-semibold text-white"
                      >
                        <ShieldCheck className="h-3.5 w-3.5" /> Restore
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          if (!confirm("Suspend this customer?")) return;
                          toggle(c.id, "suspended");
                        }}
                        className="inline-flex items-center gap-1 rounded-full bg-coral px-3 py-1.5 text-xs font-semibold text-white"
                      >
                        <ShieldOff className="h-3.5 w-3.5" /> Suspend
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </DashboardShell>
  );
}
